import { useState, useRef, useEffect } from 'react';
import { formatShortDate as formatDate } from '../../utils/dateUtils';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Title,
  Text,
  Stack,
  Group,
  SimpleGrid,
  TextInput,
  Textarea,
  Select,
  Button,
  Image,
  Loader,
  Center,
  Alert,
  Card,
  ThemeIcon,
  Badge,
  Anchor,
  Divider,
  Box,
  CopyButton,
  ActionIcon,
  Tooltip,
  Paper,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconArrowLeft,
  IconPackage,
  IconAlertCircle,
  IconStar,
  IconEye,
  IconMessage,
  IconBarcode,
  IconCopy,
  IconCheck,
  IconExternalLink,
  IconTrash,
  IconArrowMerge,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { adminService } from '../../services/admin';
import { productsService } from '../../services/products';

interface Product {
  id: string;
  barcode: string;
  name: string;
  brand?: string;
  category?: string;
  description?: string;
  imageUrl?: string;
  amazonAffiliateUrl?: string;
  reviewCount: number;
  averageRating?: number;
  viewCount?: number;
}

interface ProductReview {
  id: string;
  user: { id: string; username: string };
  rating: number;
  title: string;
  content: string;
  likeCount: number;
  commentCount: number;
  createdAt: string;
}

export default function ManageProduct() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [parentCategory, setParentCategory] = useState<string | null>(null);

  // Merge state
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeResults, setMergeResults] = useState<Product[]>([]);
  const [mergeSearching, setMergeSearching] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<Product | null>(null);
  const [merging, setMerging] = useState(false);
  const mergeSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const form = useForm({
    initialValues: {
      name: '',
      barcode: '',
      brand: '',
      category: '',
      description: '',
      imageUrl: '',
    },
  });

  const {
    data: product,
    isLoading: productLoading,
    isError: productError,
    isFetched: productFetched,
  } = useQuery({
    queryKey: ['admin-product', id],
    queryFn: async () => {
      const response = await api.get<Product>(`/products/${id}`);
      const p = response.data;
      form.setValues({
        name: p.name,
        barcode: p.barcode,
        brand: p.brand || '',
        category: p.category || '',
        description: p.description || '',
        imageUrl: p.imageUrl || '',
      });
      return p;
    },
    enabled: !!id,
    staleTime: 30_000,
  });

  const { data: categoriesGrouped = {} } = useQuery({
    queryKey: ['categories-grouped'],
    queryFn: () => productsService.getCategoriesGrouped(),
    staleTime: 300_000,
  });

  // When product loads, reverse-map its flat category string to find the parent
  useEffect(() => {
    if (!product?.category || Object.keys(categoriesGrouped).length === 0) return;
    const parent = Object.entries(categoriesGrouped).find(([, children]) =>
      children.includes(product.category!)
    )?.[0] ?? null;
    setParentCategory(parent);
  }, [product?.category, categoriesGrouped]);

  const { data: reviewsData } = useQuery({
    queryKey: ['admin-product-reviews', id],
    queryFn: () =>
      api
        .get<{ items: ProductReview[] }>(`/reviews/product/${id}`, { params: { page: 1, pageSize: 10 } })
        .then((r) => r.data),
    enabled: !!id,
    staleTime: 30_000,
  });

  const reviews = reviewsData?.items ?? [];
  const loading = productLoading;

  const handleSave = async () => {
    if (!id) return;

    setSaving(true);
    try {
      const values = form.values;
      await adminService.updateProduct(id, {
        name: values.name,
        barcode: values.barcode,
        brand: values.brand || undefined,
        category: values.category || undefined,
        description: values.description || undefined,
        imageUrl: values.imageUrl || undefined,
      });

      notifications.show({
        title: 'Saved',
        message: 'Product updated successfully',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update product',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleMergeSearch = (query: string) => {
    setMergeSearch(query);
    setMergeTarget(null);
    if (mergeSearchTimeout.current) clearTimeout(mergeSearchTimeout.current);
    if (!query.trim()) { setMergeResults([]); return; }
    mergeSearchTimeout.current = setTimeout(async () => {
      setMergeSearching(true);
      try {
        const response = await api.get<Product[]>('/products/search', { params: { q: query } });
        setMergeResults(response.data.filter((p) => p.id !== id));
      } catch {
        setMergeResults([]);
      } finally {
        setMergeSearching(false);
      }
    }, 400);
  };

  const handleMerge = async () => {
    if (!id || !product || !mergeTarget) return;
    if (!confirm(
      `Merge "${product.name}" INTO "${mergeTarget.name}"?\n\n` +
      `• All reviews from "${product.name}" will move to "${mergeTarget.name}"\n` +
      `• Duplicate reviews (same user reviewed both) will be dropped\n` +
      `• "${product.name}" will be permanently deleted\n\n` +
      `This cannot be undone.`
    )) return;

    setMerging(true);
    try {
      const result = await adminService.mergeProducts(id, mergeTarget.id);
      notifications.show({
        title: 'Merged',
        message: result.message,
        color: 'green',
      });
      navigate(`/admin/products/${mergeTarget.id}`);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to merge products',
        color: 'red',
      });
    } finally {
      setMerging(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !product) return;
    if (!confirm(`Are you sure you want to delete "${product.name}"? This will also delete all associated reviews.`)) return;

    try {
      await adminService.deleteProduct(id);
      notifications.show({
        title: 'Deleted',
        message: 'Product and associated data removed',
        color: 'green',
      });
      navigate('/admin/products');
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete product',
        color: 'red',
      });
    }
  };

  if (loading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (productError || (productFetched && !product)) {
    return (
      <Stack gap="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/admin/products')} w="fit-content">
          Back to Products
        </Button>
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          Failed to load product
        </Alert>
      </Stack>
    );
  }

  if (!product) return null;

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Group gap="xs">
          <ActionIcon variant="subtle" onClick={() => navigate('/admin/products')}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <Title order={2} lineClamp={1}>{product.name}</Title>
        </Group>
        <Anchor component={Link} to={`/products/${product.id}`} size="sm" target="_blank">
          <Group gap={4}>
            <Text size="sm">View public page</Text>
            <IconExternalLink size={14} />
          </Group>
        </Anchor>
      </Group>

      {/* Stats Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <ThemeIcon size={36} radius="md" variant="light" color="yellow">
              <IconStar size={18} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Rating</Text>
              <Text size="lg" fw={700}>{product.averageRating?.toFixed(1) || '-'}</Text>
            </div>
          </Group>
        </Card>
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <ThemeIcon size={36} radius="md" variant="light" color="blue">
              <IconMessage size={18} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Reviews</Text>
              <Text size="lg" fw={700}>{product.reviewCount}</Text>
            </div>
          </Group>
        </Card>
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <ThemeIcon size={36} radius="md" variant="light" color="green">
              <IconEye size={18} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Views</Text>
              <Text size="lg" fw={700}>{product.viewCount}</Text>
            </div>
          </Group>
        </Card>
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group gap="xs">
            <ThemeIcon size={36} radius="md" variant="light" color="gray">
              <IconBarcode size={18} />
            </ThemeIcon>
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Barcode</Text>
              <Group gap={4}>
                <Text size="lg" fw={700} ff="monospace">{product.barcode}</Text>
                <CopyButton value={product.barcode}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? 'Copied' : 'Copy'}>
                      <ActionIcon variant="subtle" color={copied ? 'green' : 'gray'} size="xs" onClick={copy}>
                        {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
            </div>
          </Group>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
        {/* Product Image & Info Card */}
        <Card shadow="sm" radius="md" withBorder p="lg">
          <Stack gap="md">
            <Text fw={600}>Product Image</Text>
            {product.imageUrl ? (
              <Box
                style={{
                  borderRadius: 'var(--mantine-radius-md)',
                  border: '1px solid var(--mantine-color-default-border)',
                  backgroundColor: 'var(--mantine-color-gray-0)',
                  overflow: 'hidden',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                h={200}
              >
                <Image src={product.imageUrl} h={200} fit="contain" />
              </Box>
            ) : (
              <Center h={200} style={{ borderRadius: 'var(--mantine-radius-md)', border: '1px dashed var(--mantine-color-default-border)' }}>
                <Stack align="center" gap={4}>
                  <IconPackage size={40} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">No image</Text>
                </Stack>
              </Center>
            )}

            <TextInput
              label="Image URL"
              placeholder="https://..."
              {...form.getInputProps('imageUrl')}
            />

            {product.amazonAffiliateUrl && (
              <>
                <Divider />
                <div>
                  <Text size="sm" fw={500} mb={4}>Amazon Affiliate Link</Text>
                  <Anchor href={product.amazonAffiliateUrl} target="_blank" size="xs" lineClamp={1}>
                    {product.amazonAffiliateUrl}
                  </Anchor>
                </div>
              </>
            )}
          </Stack>
        </Card>

        {/* Edit Details Card */}
        <Card shadow="sm" radius="md" withBorder p="lg">
          <Stack gap="md">
            <Text fw={600}>Details</Text>

            <TextInput
              label="Name"
              required
              {...form.getInputProps('name')}
            />

            <TextInput
              label="Barcode"
              required
              {...form.getInputProps('barcode')}
            />

            <TextInput
              label="Brand"
              {...form.getInputProps('brand')}
            />

            <Group grow align="flex-start">
              <Select
                label="Category"
                placeholder="Select a category"
                data={Object.keys(categoriesGrouped)}
                value={parentCategory}
                onChange={(v) => {
                  setParentCategory(v);
                  form.setFieldValue('category', '');
                }}
                clearable
                searchable
              />
              <Select
                label="Subcategory"
                placeholder={parentCategory ? 'Select a subcategory' : 'Select a category first'}
                data={parentCategory ? (categoriesGrouped[parentCategory] ?? []) : []}
                value={form.values.category || null}
                onChange={(v) => form.setFieldValue('category', v ?? '')}
                disabled={!parentCategory}
                clearable
                searchable
              />
            </Group>

            <Textarea
              label="Description"
              minRows={4}
              maxRows={8}
              autosize
              {...form.getInputProps('description')}
            />

            <Group justify="flex-end">
              <Button onClick={handleSave} loading={saving}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </Card>

        {/* Reviews Card */}
        <Card shadow="sm" radius="md" withBorder p="lg">
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Recent Reviews</Text>
              {product.reviewCount > 0 && (
                <Badge variant="light" size="sm">{product.reviewCount} total</Badge>
              )}
            </Group>

            {reviews.length === 0 ? (
              <Center py="lg">
                <Text size="sm" c="dimmed">No reviews yet</Text>
              </Center>
            ) : (
              <Stack gap="xs">
                {reviews.map((review) => (
                  <Card key={review.id} padding="sm" radius="sm" withBorder>
                    <Stack gap={4}>
                      <Group justify="space-between">
                        <Group gap={6}>
                          <Badge size="xs" color="yellow" variant="light">{review.rating}/5</Badge>
                          <Text size="xs" c="dimmed">{review.user.username}</Text>
                        </Group>
                        <Text size="xs" c="dimmed">{formatDate(review.createdAt)}</Text>
                      </Group>
                      <Text size="sm" lineClamp={2}>{review.content}</Text>
                      {(review.likeCount > 0 || review.commentCount > 0) && (
                        <Group gap="xs">
                          {review.likeCount > 0 && (
                            <Text size="xs" c="dimmed">{review.likeCount} likes</Text>
                          )}
                          {review.commentCount > 0 && (
                            <Text size="xs" c="dimmed">{review.commentCount} comments</Text>
                          )}
                        </Group>
                      )}
                    </Stack>
                  </Card>
                ))}
              </Stack>
            )}
          </Stack>
        </Card>

        {/* Merge Card */}
        <Card shadow="sm" radius="md" withBorder p="lg" style={{ borderColor: 'var(--mantine-color-orange-3)' }}>
          <Stack gap="md">
            <Group gap="xs">
              <IconArrowMerge size={18} color="var(--mantine-color-orange-6)" />
              <Text fw={600}>Merge into Another Product</Text>
            </Group>
            <Text size="sm" c="dimmed">
              Move all reviews from this product into a canonical product, then delete this one.
              Useful when the same product was added twice with slightly different names.
            </Text>

            <TextInput
              placeholder="Search for the product to merge into..."
              value={mergeSearch}
              onChange={(e) => handleMergeSearch(e.currentTarget.value)}
              rightSection={mergeSearching ? <Loader size="xs" /> : null}
            />

            {mergeResults.length > 0 && !mergeTarget && (
              <Stack gap="xs">
                {mergeResults.slice(0, 5).map((p) => (
                  <Paper
                    key={p.id}
                    p="sm"
                    radius="sm"
                    withBorder
                    style={{ cursor: 'pointer' }}
                    onClick={() => { setMergeTarget(p); setMergeResults([]); setMergeSearch(p.name); }}
                  >
                    <Group gap="sm">
                      {p.imageUrl && <Image src={p.imageUrl} w={36} h={36} radius="sm" fit="contain" />}
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>{p.name}</Text>
                        <Text size="xs" c="dimmed">{p.brand} · {p.reviewCount} reviews</Text>
                      </Stack>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            )}

            {mergeTarget && (
              <Stack gap="xs">
                <Paper p="sm" radius="sm" withBorder style={{ borderColor: 'var(--mantine-color-orange-3)' }}>
                  <Group justify="space-between">
                    <Group gap="sm">
                      {mergeTarget.imageUrl && <Image src={mergeTarget.imageUrl} w={36} h={36} radius="sm" fit="contain" />}
                      <Stack gap={2}>
                        <Text size="sm" fw={500}>{mergeTarget.name}</Text>
                        <Text size="xs" c="dimmed">{mergeTarget.brand} · {mergeTarget.reviewCount} reviews</Text>
                      </Stack>
                    </Group>
                    <Button size="xs" variant="subtle" color="gray" onClick={() => { setMergeTarget(null); setMergeSearch(''); }}>
                      Change
                    </Button>
                  </Group>
                </Paper>
                <Button
                  color="orange"
                  variant="outline"
                  leftSection={<IconArrowMerge size={16} />}
                  onClick={handleMerge}
                  loading={merging}
                  w="fit-content"
                >
                  Merge "{product.name}" into "{mergeTarget.name}"
                </Button>
              </Stack>
            )}
          </Stack>
        </Card>

        {/* Danger Zone Card */}
        <Card shadow="sm" radius="md" withBorder p="lg" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
          <Stack gap="md">
            <Text fw={600} c="red">Danger Zone</Text>
            <Text size="sm" c="dimmed">
              Deleting this product will permanently remove it along with all of its reviews, comments, and likes.
              This action cannot be undone.
            </Text>
            <Button
              color="red"
              variant="outline"
              leftSection={<IconTrash size={16} />}
              onClick={handleDelete}
              w="fit-content"
            >
              Delete Product
            </Button>
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
